import type { ImportedNode, ImportedTree } from './types';

/**
 * Parse tab- or space-indented text into an ImportedTree.
 *
 * Each non-empty line becomes a node. The indentation level determines
 * the parent-child hierarchy. Supports:
 * - Tab indentation
 * - 2-space indentation
 * - 4-space indentation
 *
 * The indent style is auto-detected from the first indented line.
 */
export function parseIndentedText(text: string): ImportedTree {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return { root: { text: 'Untitled', children: [] } };
  }

  const indentUnit = detectIndentUnit(lines);
  const parsed = lines.map((line) => ({
    level: getIndentLevel(line, indentUnit),
    text: line.trim(),
  }));

  // The first line is always the root, regardless of its indentation.
  const rootLevel = parsed[0]!.level;
  const root: ImportedNode = { text: parsed[0]!.text, children: [] };

  // Stack tracks (node, level) for parent lookups.
  const stack: Array<{ node: ImportedNode; level: number }> = [{ node: root, level: rootLevel }];

  for (let i = 1; i < parsed.length; i++) {
    const { level, text: nodeText } = parsed[i]!;
    const newNode: ImportedNode = { text: nodeText, children: [] };

    // Pop the stack until we find a parent whose level is strictly less than ours.
    while (stack.length > 1 && stack[stack.length - 1]!.level >= level) {
      stack.pop();
    }

    const parent = stack[stack.length - 1]!;
    parent.node.children.push(newNode);
    stack.push({ node: newNode, level });
  }

  return { root };
}

type IndentUnit = { type: 'tab' } | { type: 'spaces'; count: number };

function detectIndentUnit(lines: string[]): IndentUnit {
  for (const line of lines) {
    if (line.startsWith('\t')) {
      return { type: 'tab' };
    }
    const match = /^( +)\S/.exec(line);
    if (match) {
      const spaceCount = match[1]!.length;
      // Use the first indented line's space count as the unit.
      // Clamp to 2 or 4 for common cases; otherwise use the raw count.
      if (spaceCount <= 2) return { type: 'spaces', count: 2 };
      if (spaceCount <= 4) return { type: 'spaces', count: 4 };
      return { type: 'spaces', count: spaceCount };
    }
  }
  // Default to tab if no indentation found.
  return { type: 'tab' };
}

function getIndentLevel(line: string, unit: IndentUnit): number {
  if (unit.type === 'tab') {
    let count = 0;
    for (const ch of line) {
      if (ch === '\t') count++;
      else break;
    }
    return count;
  }

  let spaceCount = 0;
  for (const ch of line) {
    if (ch === ' ') spaceCount++;
    else break;
  }
  return Math.floor(spaceCount / unit.count);
}
